/**
 * Renderer for the `ask_user_question` tool — turns the persisted tool
 * call + result into a styled in-history bubble per issue #975's wireframe.
 *
 * Two visible states:
 *   - **Awaiting answer** (output absent) — the active card is rendered in
 *     the composer slot, so here we just show a muted "Asking question…"
 *     line so the timeline reads coherently after a reload-mid-pending.
 *   - **Answered / dismissed** — show each question's chosen option(s)
 *     with the description, or a muted "Dismissed" badge when the user
 *     declined. Mirrors the wireframe's user-side bubble look.
 *
 * @module messages/tool-call-message/renderers/ask-question-renderer
 */

import { MinusCircle } from "lucide-react"
import { useMemo } from "react"
import type {
  AskUserAnswers,
  AskUserNotes,
  AskUserQuestion,
  AskUserQuestionInput,
} from "@/types/ask-question"

interface AskQuestionRendererProps {
  /** Tool input: the questions the agent asked. */
  input?: Partial<AskUserQuestionInput>
  /** Tool output: either { answers, notes? } or { declined: true }. Absent while pending. */
  output?: {
    answers?: AskUserAnswers
    notes?: AskUserNotes
    declined?: boolean
  }
}

function stripRecommendedSuffix(label: string): string {
  return label.replace(/\s*\(\s*recommended\s*\)\s*$/i, "").trim()
}

/**
 * Splits the persisted answer string back into the individual picked
 * labels. The card joins multi-selects with ", " when it builds the
 * answers map, so the same separator is the right split key.
 */
function parseAnswerLabels(value: string): string[] {
  return value
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean)
}

export function AskQuestionRenderer({ input, output }: AskQuestionRendererProps) {
  const questions = (input?.questions ?? []) as AskUserQuestion[]

  // No questions to render against (defensive only).
  if (questions.length === 0) return null

  // The tool call lands as TWO message parts: one on the assistant message
  // (input only, no output) and one on the follow-up tool/user message
  // (input + output once the user answers). Hide the input-only half — the
  // live card already lives in the composer slot, so showing a duplicate
  // "asking…" line in the timeline is noisy. The answered bubble below
  // covers the persistent state.
  if (!output) return null

  // Dismissed → muted single-line badge.
  if (output.declined) {
    return (
      <div className="my-2 flex justify-end">
        <div className="inline-flex items-center gap-2 rounded-xl bg-muted px-3 py-2 text-xs text-muted-foreground">
          <MinusCircle className="size-3.5" />
          <span>Dismissed</span>
        </div>
      </div>
    )
  }

  // Answered → right-aligned user-style bubble per question so the picked
  // answer reads as a normal user reply in the timeline (matches the
  // `message.tsx` user-bubble look: rounded-xl bg-muted, px-3 py-2, ml-auto).
  return (
    <div className="my-3 flex flex-col items-end gap-2">
      {questions.map((q, qi) => (
        <AnsweredBubble
          key={qi}
          question={q}
          answer={output.answers?.[q.question]}
          note={output.notes?.[q.question]}
        />
      ))}
    </div>
  )
}

interface AnsweredBubbleProps {
  question: AskUserQuestion
  answer?: string
  note?: string
}

function AnsweredBubble({ question: _question, answer, note }: AnsweredBubbleProps) {
  // Normalize the persisted answer into a single display line. Multi-select
  // labels come back joined with ", "; "Other: …" answers are kept verbatim
  // so the free-text shows up. "(Recommended)" suffixes are stripped because
  // they're a card-only hint, not part of the user's intent.
  const displayText = useMemo(() => {
    if (!answer) return null
    const labels = parseAnswerLabels(answer)
    return labels
      .map((label) => {
        if (label.toLowerCase().startsWith("other:")) {
          const rest = label.replace(/^other:\s*/i, "").trim()
          return rest || "Other"
        }
        return stripRecommendedSuffix(label)
      })
      .join(", ")
  }, [answer])

  if (!displayText) return null

  return (
    <div className="rounded-xl bg-muted px-3 py-2 text-foreground max-w-2xl">
      <p className="whitespace-pre-wrap break-words">{displayText}</p>
      {note ? (
        <p className="mt-1 text-xs italic text-muted-foreground">{note}</p>
      ) : null}
    </div>
  )
}
